import { DeviceCode, GroupSecurity } from '@quromedical/fhir-common';
import { Title } from 'components/card';
import { WithGroup } from 'core/auth';
import { BandStatusCard, RelayStatus, StatusCard } from 'core/device';
import { DeviceUseStatementList } from 'core/device-use-statement';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Text,
  Visible,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import type { DeviceStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';
import { trpc } from 'trpc';

import { DeviceCard } from './DeviceCard';
import { DeviceConfigure } from './DeviceConfigure';
import { DeviceMetricsList } from './DeviceMetricsList';

type RouteParams = {
  id: string;
};

export const DeviceScreen: React.FC<DeviceStackScreenProps<'View'>> = () => {
  const { id } = useLocalSearchParams<RouteParams>();

  const {
    data,
    isFetching: isValidating,
    isError: error,
    refetch,
  } = trpc.device.getById.useQuery({ id });

  const isRelayDevice = data?.type === DeviceCode.relay;
  const isPatchDevice = data?.type === DeviceCode.patch;
  const isBandDevice = data?.type === DeviceCode.band;

  const revalidate = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <ScrollView>
      <SkeletonProvider loading={isValidating}>
        <Visible if={error}>
          <Snackbar onClose={revalidate} isOpen={!!error}>
            <Alert
              backgroundColor="status-critical"
              textColor="white"
              onAction={revalidate}
              actionIcon="close"
              title={strings.ErrorCardTitle}
              body={strings.GenericErrorMessage}
            />
          </Snackbar>
        </Visible>
        <Alert />
        <Section unsetZIndex>
          <Skeleton>
            <DeviceCard id={id} device={data} revalidate={revalidate} />
          </Skeleton>
        </Section>
        <WithGroup groups={[GroupSecurity.DeviceConfigure]}>
          {/* Feature hidden because functionality does not work. */}
          <Section unsetZIndex isVisible={false}>
            <DeviceConfigure
              inline
              deviceId={id}
              deviceCode={data?.type}
              revalidate={revalidate}
            />
          </Section>
        </WithGroup>
        <Section isVisible={isRelayDevice}>
          <Skeleton>
            <RelayStatus deviceId={id} device={data} />
          </Skeleton>
        </Section>
        <Section isVisible={isRelayDevice}>
          <StatusCard deviceId={id} />
        </Section>
        <Section isVisible={isBandDevice}>
          <BandStatusCard deviceId={id} />
        </Section>
        <Section hasBottomMargin>
          <Skeleton>
            <Title title="Associated Patients" />
            <DeviceUseStatementList deviceId={id} />
          </Skeleton>
        </Section>
        <WithGroup groups={[GroupSecurity.DeviceMetricRead]}>
          <Section isVisible={isPatchDevice}>
            <Text>{strings.DeviceMetricListTitle}</Text>
          </Section>
          <Section isVisible={isPatchDevice}>
            <DeviceMetricsList deviceId={id} />
          </Section>
        </WithGroup>
      </SkeletonProvider>
    </ScrollView>
  );
};
